import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Star, Upload, Send, CheckCircle, ExternalLink, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ReviewFormData {
  reviewer_name: string;
  rating: number;
  review_text: string;
}

interface ReviewFormProps {
  productId: string;
  productName: string;
  onReviewSubmitted?: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  productId,
  productName,
  onReviewSubmitted
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewImage, setReviewImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ReviewFormData>();

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setReviewImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setReviewImage(null);
    setImagePreview(null);
  };

  const onSubmit = async (data: ReviewFormData) => {
    setIsSubmitting(true);

    try {
      let imageUrl = null;

      // Upload image if provided
      if (reviewImage) {
        const fileExt = reviewImage.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `reviews/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('instruments')
          .upload(filePath, reviewImage);

        if (uploadError) {
          console.error('Error uploading image:', uploadError);
        } else {
          imageUrl = filePath;
        }
      }

      // Save review to database
      const { error } = await supabase
        .from('product_reviews')
        .insert({
          product_id: productId,
          reviewer_name: data.reviewer_name,
          rating: selectedRating,
          review_text: data.review_text,
          image_url: imageUrl,
          is_verified: false,
          status: 'pending'
        });

      if (error) {
        console.error('Error saving review:', error);
        throw error;
      }

      setSubmitted(true);
      reset();
      setSelectedRating(0);
      setReviewImage(null);
      setImagePreview(null);
      
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }

      // Auto-hide success message after 10 seconds
      setTimeout(() => {
        setSubmitted(false);
      }, 10000);

    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (interactive: boolean = false) => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = index + 1;
      const isFilled = interactive 
        ? starValue <= (hoverRating || selectedRating)
        : starValue <= selectedRating;

      return (
        <button
          key={index}
          type="button"
          onClick={() => interactive && setSelectedRating(starValue)}
          onMouseEnter={() => interactive && setHoverRating(starValue)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-all duration-200`}
          disabled={!interactive}
        >
          <Star
            className={`h-6 w-6 ${
              isFilled ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        </button>
      );
    });
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-green-50 border border-green-200 rounded-lg p-6"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          
          <h3 className="text-lg font-semibold text-green-800 mb-2">
            Thank You for Your Review!
          </h3>
          
          <p className="text-green-700 mb-6">
            Your review has been submitted and will be published after moderation. 
            We appreciate your feedback!
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-blue-800 mb-2">Review Under Moderation</h4>
            <p className="text-sm text-blue-700">
              Your review is currently being reviewed by our team to ensure quality and authenticity. 
              Approved reviews will appear on the product page within 24-48 hours.
            </p>
          </div>

          {/* Google Review Link */}
          <div className="bg-white border border-green-300 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-gray-900 mb-2">
              Help Others Find Us!
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              Would you also like to leave a review on Google to help other musicians discover Strum Music?
            </p>
            <a
              href="https://g.page/r/CeVEJKJKU1CwEBM/review"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md transition-colors"
            >
              <span>Leave Google Review</span>
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>

          <button
            onClick={() => setSubmitted(false)}
            className="btn btn-outline py-2 px-4"
          >
            Write Another Review
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Leave a Review</h3>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Name Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Name *
          </label>
          <input
            {...register('reviewer_name', { required: 'Name is required' })}
            className="input w-full"
            placeholder="Enter your name"
          />
          {errors.reviewer_name && (
            <p className="text-red-600 text-sm mt-1">{errors.reviewer_name.message}</p>
          )}
        </div>

        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rating *
          </label>
          <div className="flex items-center space-x-1">
            {renderStars(true)}
            <span className="ml-2 text-sm text-gray-600">
              {selectedRating > 0 ? `${selectedRating} star${selectedRating !== 1 ? 's' : ''}` : 'Select rating'}
            </span>
          </div>
          {selectedRating === 0 && (
            <p className="text-red-600 text-sm mt-1">Please select a rating</p>
          )}
        </div>

        {/* Review Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Review *
          </label>
          <textarea
            {...register('review_text', { 
              required: 'Review text is required',
              minLength: {
                value: 10,
                message: 'Review must be at least 10 characters long'
              }
            })}
            rows={4}
            className="input w-full"
            placeholder={`Share your experience with the ${productName}...`}
          />
          {errors.review_text && (
            <p className="text-red-600 text-sm mt-1">{errors.review_text.message}</p>
          )}
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add Photo (Optional)
          </label>
          
          {!imagePreview ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-2">
                Upload a photo of your product
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                id="review-image"
              />
              <label
                htmlFor="review-image"
                className="btn btn-outline py-2 px-4 cursor-pointer"
              >
                Choose File
              </label>
            </div>
          ) : (
            <div className="relative">
              <img
                src={imagePreview}
                alt="Review preview"
                className="w-full h-32 object-cover rounded-lg border border-gray-300"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || selectedRating === 0}
            className="btn btn-primary py-2 px-6 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>Submit Review</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;